import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, Box, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip } from '@mui/material';
import { District } from '../../app/feathers/districts/District';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { Warning, ExpandMore, UnfoldMore, UnfoldLess, Visibility, Search, UploadFile } from '@mui/icons-material';
import { stateCodeForName } from '../../app/util/clean-states';
import { TypeDropdown } from '../TypeDropdown';

interface ShapefileComposerProps {
  districts: District[];
  onChangeComposition: (composition: Record<string, District[]>) => void;
}

const TwoSideSplit = styled.div`
  display: flex;
  gap: 16px;
  width: calc(100% - 16px);
  border-radius: 8px;
  padding: 8px;
  background-color:rgba(230, 242, 249, 0.43);
`;

const SplitInner = styled.div<{ $grow?: number }>`
  flex: ${props => props.$grow || 1};
  min-width: 0;
`;

const SelectedTableRow = styled(TableRow)<{ selected?: boolean }>`
  background-color: ${props => props.selected ? '#e3f2fd' : 'inherit'};
  cursor: pointer;
  &:hover {
    background-color: ${props => props.selected ? '#e3f2fd' : '#f5f5f5'};
  }
`;

const SelectedTypography = styled(Typography)<{ selected?: boolean }>`
  font-weight: ${props => props.selected ? 'bold' : 'normal'};
`;

const AccordionWrapper = styled.div`
  margin-top: 0;
  width: 100%;
`;

const BuildingBlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 0;
`;

const SubtleAccordion = styled(Accordion)`
  box-shadow: none;
  &:before {
    display: none;
  }
  .MuiAccordionSummary-root {
    min-height: 24px;
    padding: 0 16px;
  }
  .MuiAccordionSummary-content {
    margin: 4px 0;
  }
  .MuiAccordionDetails-root {
    padding: 0 16px 8px;
  }
`;

const SubtleExpandIcon = styled(ExpandMore)`
  font-size: 16px;
  color: #999;
`;

const missingGeoboundaryTooltip = 'Districts without districts cannot be used as building blocks.';

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(224, 224, 224, 1);
`;

export const ShapefileComposer: React.FC<ShapefileComposerProps> = ({ districts: targetDistricts, onChangeComposition }) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration>();
  const [stateDistrictId, setStateDistrictId] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sourceTier, setSourceTier] = useState<number | null>(null);
  const [buildingBlockDistricts, setBuildingBlockDistricts] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [selectedTargetDistrict, setSelectedTargetDistrict] = useState<District | null>(null);
  const [selectedBuildingBlocks, setSelectedBuildingBlocks] = useState<Record<string, string[]>>({});
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});
  const [showUsedBlocks, setShowUsedBlocks] = useState(true);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [showUploadConfigModal, setShowUploadConfigModal] = useState(false);
  const [uploadConfigText, setUploadConfigText] = useState('');
  const [uploadConfigError, setUploadConfigError] = useState<string | null>(null);

  // Clear all selections and state when district type selection changes
  useEffect(() => {
    setSelectedTargetDistrict(null);
    setSelectedBuildingBlocks({});
    setExpandedAccordions({});
    setLeftSearch('');
    setRightSearch('');
    setBuildingBlockDistricts([]);
    onChangeComposition({});
  }, [selectedTypes]);

  useEffect(() => {
    async function loadStateConfiguration() {
      if (!feathers || targetDistricts.length === 0) return;
      
      try {
        // Recursively get parent districts until we find a state
        async function findStateDistrict(district: District): Promise<District> {
          if (district.type === 'state') {
            return district;
          }
          
          if (!district.parentId) {
            throw new Error('Could not find state in district hierarchy');
          }
          
          const parentDistrict = await feathers.getService('districts').get(district.parentId);
          return findStateDistrict(parentDistrict);
        }

        const stateDistrict = await findStateDistrict(targetDistricts[0]);
        setStateDistrictId(stateDistrict._id);
        const stateCode = stateCodeForName(stateDistrict.name);
        const config = await feathers.getService('state-configurations').get(stateCode.toLowerCase());
        setStateConfiguration(config);

        // Validate that all districts are of the same tier
        const firstDistrictType = targetDistricts[0].type;
        const firstDistrictTier = config.typeConfigurations.find(tc => tc.type === firstDistrictType)?.tierNumber;
        
        if (firstDistrictTier === undefined) {
          throw new Error(`Unknown district type: ${firstDistrictType}`);
        }

        const allSameTier = targetDistricts.every(district => {
          const tier = config.typeConfigurations.find(tc => tc.type === district.type)?.tierNumber;
          return tier === firstDistrictTier;
        });

        if (!allSameTier) {
          throw new Error('All districts must be of the same tier');
        }

        setSourceTier(firstDistrictTier);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load state configuration');
        console.error('Error loading state configuration:', err);
      }
    }
    loadStateConfiguration();
  }, [feathers, targetDistricts]);

  // Load districts when type is selected
  useEffect(() => {
    async function loadDistricts() {
      if (!feathers || selectedTypes.length === 0 || !stateDistrictId) return;

      try {
        setLoadingDistricts(true);
        const allDistricts: District[] = [];
        let skip = 0;
        const limit = 100;
        let hasMore = true;

        while (hasMore) {
          const response = await feathers.getService('districts').find({
            query: {
              type: { $in: selectedTypes },
              state: stateDistrictId,
              $includeGeoDistrict: true,
              $sort: { createdAt: 1 },
              $skip: skip,
              $limit: limit
            }
          });

          allDistricts.push(...response.data);
          skip += limit;
          hasMore = response.data.length === limit;
        }

        setBuildingBlockDistricts(allDistricts);
      } catch (err) {
        console.error('Error loading districts:', err);
        setError('Failed to load districts');
      } finally {
        setLoadingDistricts(false);
      }
    }

    loadDistricts();
  }, [feathers, selectedTypes, stateDistrictId]);

  const districtTypes = stateConfiguration?.typeConfigurations || [];

  // Filter district types based on source tier
  const filteredDistrictTypes = useMemo(() => {
    if (!sourceTier || !stateConfiguration) return [];

    return districtTypes.filter(type => {
      // Don't show tier 0 or 1 types
      if (!type.tierNumber || type.tierNumber <= 1) return false;

      // If source is tier 2, show tier 2 and tier 3 types
      if (sourceTier === 2 && type.tierNumber > 3) return false;

      // If source is tier 3, show tier 2 and 3 types
      if (sourceTier === 3 && type.tierNumber > 3) return false;

      return true;
    });
  }, [districtTypes, sourceTier, stateConfiguration]);

  const availableDistrictTypes = useMemo(() => {
    return filteredDistrictTypes.filter(type => !selectedTypes.includes(type.type));
  }, [filteredDistrictTypes, selectedTypes]);

  const addSelectedType = (newType: string) => {
    if (selectedTypes.includes(newType)) return;
    setSelectedTypes([...selectedTypes, newType]);
  };

  const removeSelectedType = (typeToRemove: string) => {
    setSelectedTypes(selectedTypes.filter(type => type !== typeToRemove));
  };

  const handleDistrictSelect = (district: District) => {
    if (selectedTargetDistrict?._id === district._id) {
      setSelectedTargetDistrict(null);
    } else {
      setSelectedTargetDistrict(district);
    }
  };

  const handleBuildingBlockSelect = (buildingBlockDistrict: District) => {
    if (!selectedTargetDistrict) return;

    setSelectedBuildingBlocks(prev => {
      const currentBlocks = prev[selectedTargetDistrict._id] || [];
      const isSelected = currentBlocks.includes(buildingBlockDistrict._id);
      
      let newBlocks;
      if (isSelected) {
        // Remove if already selected
        newBlocks = {
          ...prev,
          [selectedTargetDistrict._id]: currentBlocks.filter(id => id !== buildingBlockDistrict._id)
        };
      } else {
        // Add if not selected
        newBlocks = {
          ...prev,
          [selectedTargetDistrict._id]: [...currentBlocks, buildingBlockDistrict._id]
        };
      }

      // Convert the string IDs to actual District objects for the parent component
      const composition: Record<string, District[]> = {};
      Object.entries(newBlocks).forEach(([targetId, blockIds]) => {
        composition[targetId] = blockIds.map(id => 
          buildingBlockDistricts.find(d => d._id === id)
        ).filter((d): d is District => d !== undefined);
      });

      onChangeComposition(composition);
      return newBlocks;
    });
  };

  const isBuildingBlockSelected = (buildingBlockDistrict: District) => {
    if (!selectedTargetDistrict) return false;
    const selectedBlocks = selectedBuildingBlocks[selectedTargetDistrict._id] || [];
    return selectedBlocks.includes(buildingBlockDistrict._id);
  };

  const getBuildingBlockCount = (targetDistrictId: string) => {
    return (selectedBuildingBlocks[targetDistrictId] || []).length;
  };

  const getBuildingBlockNames = (targetDistrictId: string) => {
    const selectedIds = selectedBuildingBlocks[targetDistrictId] || [];
    return buildingBlockDistricts
      .filter(district => selectedIds.includes(district._id))
      .map(district => district.longName);
  };

  const handleAccordionChange = (districtId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [districtId]: isExpanded
    }));
  };

  const toggleAllAccordions = () => {
    const allExpanded = Object.values(expandedAccordions).every(Boolean);
    const newState = targetDistricts.reduce((acc, district) => ({
      ...acc,
      [district._id]: !allExpanded
    }), {});
    setExpandedAccordions(newState);
  };

  const getBuildingBlockUsageCount = (buildingBlockId: string) => {
    return Object.values(selectedBuildingBlocks).filter(blocks => 
      blocks.includes(buildingBlockId)
    ).length;
  };

  const getDistrictIdentifier = (district: District) => {
    const identifier = district?.name || district?.number || district?.letter;
    return typeof identifier === 'number' ? String(identifier) : (identifier || '').trim();
  };

  const districtMissingGeoboundary = (district: District) => (
    district.hasGeodistrict === false || (district.geoDistrict ? !district.geoDistrict.boundary : false)
  );

  const normalizeIdentifier = (identifier: string) => identifier.trim().toLowerCase();

  const parseCsvLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values;
  };

  const buildCompositionFromSelection = (blocksByTarget: Record<string, string[]>) => {
    const nextComposition: Record<string, District[]> = {};
    Object.entries(blocksByTarget).forEach(([targetId, blockIds]) => {
      nextComposition[targetId] = blockIds
        .map((id) => buildingBlockDistricts.find((district) => district._id === id))
        .filter((district): district is District => Boolean(district));
    });
    return nextComposition;
  };

  const applyUploadedConfig = () => {
    if (selectedTypes.length === 0) {
      setUploadConfigError('Select at least one district type to build from before uploading a config.');
      return;
    }
    if (!uploadConfigText.trim()) {
      setUploadConfigError('Paste CSV content before submitting.');
      return;
    }

    const targetByIdentifier = targetDistricts.reduce((acc, district) => {
      const identifier = getDistrictIdentifier(district);
      if (identifier) {
        acc[normalizeIdentifier(identifier)] = district;
      }
      return acc;
    }, {} as Record<string, District>);

    const blockByIdentifier = buildingBlockDistricts.reduce((acc, district) => {
      const identifier = getDistrictIdentifier(district);
      if (identifier) {
        acc[normalizeIdentifier(identifier)] = district;
      }
      return acc;
    }, {} as Record<string, District>);

    const lines = uploadConfigText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      setUploadConfigError('No rows found in CSV text.');
      return;
    }

    const parsedRows = lines.map((line) => parseCsvLine(line));
    const hasHeaderRow = parsedRows.length > 0 && !targetByIdentifier[normalizeIdentifier(parsedRows[0][0] || '')];
    const dataRows = hasHeaderRow ? parsedRows.slice(1) : parsedRows;

    const nextSelectedBlocks: Record<string, string[]> = { ...selectedBuildingBlocks };
    const rowErrors: string[] = [];

    dataRows.forEach((row, rowIndex) => {
      const targetIdentifierRaw = (row[0] || '').trim();
      const buildingBlocksCell = row.slice(1).join(',').trim();
      const displayRow = hasHeaderRow ? rowIndex + 2 : rowIndex + 1;

      if (!targetIdentifierRaw) {
        rowErrors.push(`Row ${displayRow}: missing target district identifier.`);
        return;
      }

      const targetDistrict = targetByIdentifier[normalizeIdentifier(targetIdentifierRaw)];
      if (!targetDistrict) {
        rowErrors.push(`Row ${displayRow}: target district "${targetIdentifierRaw}" not found.`);
        return;
      }

      const blockIdentifiers = buildingBlocksCell
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      if (blockIdentifiers.length === 0) {
        nextSelectedBlocks[targetDistrict._id] = [];
        return;
      }

      const unmatchedBlocks: string[] = [];
      const matchedBlockIds = blockIdentifiers.reduce((acc, blockIdentifier) => {
        const blockDistrict = blockByIdentifier[normalizeIdentifier(blockIdentifier)];
        if (!blockDistrict) {
          unmatchedBlocks.push(blockIdentifier);
          return acc;
        }
        if (!acc.includes(blockDistrict._id)) {
          acc.push(blockDistrict._id);
        }
        return acc;
      }, [] as string[]);

      if (unmatchedBlocks.length > 0) {
        rowErrors.push(
          `Row ${displayRow}: could not match building blocks (${unmatchedBlocks.join(', ')}).`
        );
        return;
      }

      nextSelectedBlocks[targetDistrict._id] = matchedBlockIds;
    });

    if (rowErrors.length > 0) {
      setUploadConfigError(rowErrors.join(' '));
      return;
    }

    setSelectedBuildingBlocks(nextSelectedBlocks);
    onChangeComposition(buildCompositionFromSelection(nextSelectedBlocks));
    setUploadConfigError(null);
    setShowUploadConfigModal(false);
    setUploadConfigText('');
  };

  const filteredTargetDistricts = useMemo(() => {
    if (!leftSearch) return targetDistricts;
    const searchLower = leftSearch.toLowerCase();
    return targetDistricts.filter(district => 
      district.longName.toLowerCase().includes(searchLower)
    );
  }, [targetDistricts, leftSearch]);

  const filteredBuildingBlocks = useMemo(() => {
    let filtered = buildingBlockDistricts;
    
    // Filter by search term
    if (rightSearch) {
      const searchLower = rightSearch.toLowerCase();
      filtered = filtered.filter(district => 
        district.longName.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by usage
    if (!showUsedBlocks) {
      filtered = filtered.filter(district => 
        getBuildingBlockUsageCount(district._id) === 0
      );
    }
    
    return filtered;
  }, [buildingBlockDistricts, rightSearch, showUsedBlocks, selectedBuildingBlocks]);

  if (error) {
    return (
      <div>
        <Typography color="error" style={{ marginBottom: '16px' }}>
          {error}
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <TwoSideSplit>
        <SplitInner $grow={1}>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search districts..."
              value={leftSearch}
              onChange={(e) => setLeftSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: '18px', color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: '200px',
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  fontSize: '0.875rem',
                  borderRadius: '16px',
                }
              }}
            />
            <Tooltip title={Object.values(expandedAccordions).every(Boolean) ? 'Collapse All' : 'Expand All'}>
              <IconButton 
                size="small" 
                onClick={toggleAllAccordions}
                color="primary"
              >
                {Object.values(expandedAccordions).every(Boolean) ? <UnfoldLess /> : <UnfoldMore />}
              </IconButton>
            </Tooltip>
          </Box>
          <TableContainer component={Paper} style={{ width: '100%', overflowX: 'scroll' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>District Name</TableCell>
                  <TableCell align="right" style={{ width: '120px' }}>Building Blocks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTargetDistricts.map((district) => (
                  <SelectedTableRow 
                    key={district._id}
                    selected={selectedTargetDistrict?._id === district._id}
                    onClick={() => handleDistrictSelect(district)}
                  >
                    <TableCell>
                      <SelectedTypography
                        variant="body1"
                        selected={selectedTargetDistrict?._id === district._id}
                      >
                        {district.longName}
                      </SelectedTypography>
                    </TableCell>
                    <TableCell align="right" style={{ padding: 0 }}>
                      <AccordionWrapper>
                        <SubtleAccordion
                          expanded={expandedAccordions[district._id] || false}
                          onChange={handleAccordionChange(district._id)}
                        >
                          <AccordionSummary
                            expandIcon={<SubtleExpandIcon />}
                            aria-controls={`building-blocks-${district._id}`}
                            id={`building-blocks-${district._id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Typography variant="caption" color="textSecondary">
                              {getBuildingBlockCount(district._id) > 0 
                                ? `${getBuildingBlockCount(district._id)} selected`
                                : 'None selected'}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <BuildingBlockList>
                              {getBuildingBlockNames(district._id).map((name, index) => (
                                <Typography key={index} variant="caption" color="textSecondary">
                                  {name}
                                </Typography>
                              ))}
                            </BuildingBlockList>
                          </AccordionDetails>
                        </SubtleAccordion>
                      </AccordionWrapper>
                    </TableCell>
                  </SelectedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SplitInner>
        
        <SplitInner $grow={1.35}>
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, flex: 1, minWidth: '220px' }}>
              {selectedTypes.map((type) => (
                <Chip
                  key={type}
                  label={humanizeDistrictType(type, districtTypes)}
                  onDelete={() => removeSelectedType(type)}
                  size="small"
                />
              ))}
              <TypeDropdown
                placeholder="Add type"
                value={null}
                options={availableDistrictTypes.map((type) => ({
                  type: type.type,
                  title: humanizeDistrictType(type.type, districtTypes)
                }))}
                onChange={addSelectedType}
              />
            </Box>
            <TextField
              size="small"
              placeholder="Search districts..."
              value={rightSearch}
              onChange={(e) => setRightSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: '18px', color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: '200px',
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  fontSize: '0.875rem',
                  borderRadius: '16px',
                }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {selectedTypes.length > 0 && (
                <Tooltip title='Upload config CSV'>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setUploadConfigError(null);
                      setShowUploadConfigModal(true);
                    }}
                  >
                    <UploadFile />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={showUsedBlocks ? 'Hide used blocks' : 'Show all blocks'}>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => setShowUsedBlocks(!showUsedBlocks)}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {loadingDistricts ? (
            <Paper style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', maxHeight: '200px' }}>
              <CircularProgress />
            </Paper>
          ) : selectedTypes.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>District Name</TableCell>
                      <TableCell align="right">Usage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBuildingBlocks.map((district) => {
                      const usageCount = getBuildingBlockUsageCount(district._id);
                      return (
                        <SelectedTableRow 
                          key={district._id}
                          selected={isBuildingBlockSelected(district)}
                          onClick={() => handleBuildingBlockSelect(district)}
                          style={{ cursor: selectedTargetDistrict ? 'pointer' : 'default' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SelectedTypography 
                                variant="body1"
                                selected={isBuildingBlockSelected(district)}
                              >
                                {district.longName}
                              </SelectedTypography>
                              {districtMissingGeoboundary(district) && (
                                <Tooltip title={missingGeoboundaryTooltip}>
                                  <Warning color="error" style={{ fontSize: '20px' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {usageCount > 0 && (
                              <Typography 
                                variant="caption" 
                                color={usageCount > 1 ? 'error' : 'textSecondary'}
                              >
                                Used in {usageCount} {usageCount === 1 ? 'district' : 'districts'}
                              </Typography>
                            )}
                          </TableCell>
                        </SelectedTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Paper style={{ padding: '16px', minHeight: '200px', maxHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                Select a district type to see available districts
              </Typography>
            </Paper>
          )}
        </SplitInner>
      </TwoSideSplit>
      <Dialog
        open={showUploadConfigModal}
        onClose={() => setShowUploadConfigModal(false)}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Upload composition config</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Paste raw CSV where column 1 is the target district identifier and column 2 is the building block identifiers.
            District identifiers are matched by district.name, district.number, or district.letter.
          </Typography>
          <TextField
            multiline
            minRows={10}
            fullWidth
            placeholder={'circuit_number,counties\n1,Choctaw Clarke Washington\n2,Butler Crenshaw Lowndes'}
            value={uploadConfigText}
            onChange={(event) => setUploadConfigText(event.target.value)}
          />
          {uploadConfigError && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              {uploadConfigError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadConfigModal(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={applyUploadedConfig}
            disabled={selectedTypes.length === 0 || !buildingBlockDistricts.length}
          >
            Apply config
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};